import { createStore, applyMiddleware, compose } from "redux"
import rootReducer from "../reducers/index"
import initialState from './initial-state'
import msEs from '../translations/es.json'
import msEn from '../translations/en.json'
import { addLocaleData } from 'react-intl'
import thunk from 'redux-thunk'
import esLocaleData from 'react-intl/locale-data/es'
import enLocaleData from 'react-intl/locale-data/en'
import { VERSION } from '../utils'

addLocaleData([
  ...esLocaleData,
  ...enLocaleData
])

const ASSET_COLLECTION_KEYS = [
  'assetsRoot',
  'assetsExplore',
  'assetsLastUploads',
  'assetsFavorites',
  'assetsShared',
  'assetsPublicShared',
  'searcher',
]

const TRANSIENT_ASSET_FLAGS = [
  'isCheckMultiple',
  'isSelected',
  'isEditSelected',
  'isShareSelected',
  'isExpandSelected',
]

const MESSAGES_BY_LOCALE = { es: msEs, en: msEn }

function sanitizePersistedState(state) {
  if (!state) return state

  const sanitized = {
    ...state,
    // Los mensajes de traducción son datos estáticos del build, no del
    // usuario: si vienen de un localStorage viejo, una key agregada después
    // (como rights_detail_expired_days) queda invisible hasta que el guard
    // de versión invalide el estado — algo que no pasa en cada release chico.
    // Se refrescan siempre desde el bundle actual, conservando el idioma elegido.
    intl: state.intl
      ? {
          ...state.intl,
          messages: MESSAGES_BY_LOCALE[state.intl.locale] || msEs,
        }
      : state.intl,
    selectedFiles: state.selectedFiles
      ? {
          ...state.selectedFiles,
          data: [],
          keyCollection: '',
          showModalMultiDownload: false,
          showModalMultiShare: false,
          showModalMultiDelete: false,
          showModalMultiMove: false,
        }
      : state.selectedFiles,
    individualSelect: state.individualSelect
      ? {
          ...state.individualSelect,
          selectedFile: null,
          action: '',
          keyCollection: '',
          section: '',
        }
      : state.individualSelect,
  }

  ASSET_COLLECTION_KEYS.forEach(key => {
    const collection = state[key]
    if (!collection || !Array.isArray(collection.data)) return

    sanitized[key] = {
      ...collection,
      activateMultiOptions: false,
      data: collection.data.map(item => {
        const cleanItem = { ...item }
        TRANSIENT_ASSET_FLAGS.forEach(flag => delete cleanItem[flag])
        return cleanItem
      }),
    }
  })

  return sanitized
}

/**
 * Completa el estado rehidratado con las claves que le falten del initial-state.
 *
 * El estado persistido se pasa a createStore como preloadedState, y un reducer
 * sólo cae en su valor por defecto cuando su slice es `undefined`. Si la slice
 * existe pero le falta una clave que agregamos después —porque el usuario tiene
 * en localStorage un estado guardado por una versión anterior del front—, esa
 * clave queda en undefined y el componente que la lee revienta. Pasó con
 * `rights.list`: entrar a la vista de lista dejaba la pantalla en blanco.
 *
 * `VERSION` (la de package.json) descarta el estado sólo cuando sube el número
 * de versión, así que no cubre un deploy que agrega campos sin cambiarla.
 *
 * El persistido siempre gana: acá sólo se agregan claves ausentes, nunca se
 * pisa un valor guardado (ni siquiera null o false). Los arrays se dejan tal
 * cual: mezclar sus elementos con los del default sería inventar datos.
 */
function completarConDefaults(persistido, defaults) {
  if (Array.isArray(persistido) || Array.isArray(defaults)) return persistido
  if (!esObjetoPlano(persistido) || !esObjetoPlano(defaults)) return persistido

  const resultado = { ...persistido }
  Object.keys(defaults).forEach(clave => {
    if (!(clave in resultado)) {
      resultado[clave] = defaults[clave]
    } else {
      resultado[clave] = completarConDefaults(resultado[clave], defaults[clave])
    }
  })
  return resultado
}

function esObjetoPlano(valor) {
  return !!valor && typeof valor === 'object' && !Array.isArray(valor)
}

function saveToLocalStorage(state) {
  try {
    const serializedState = JSON.stringify(sanitizePersistedState(state))
    localStorage.setItem('state', serializedState)
  } catch (e) { }
}

function loadFromLocalStorage() {
  try {
    const currentVersion = localStorage.getItem('version')
    if (currentVersion !== VERSION) {
      return undefined
    }
    const serializedState = localStorage.getItem('state')
    if (serializedState === null) return undefined
    return completarConDefaults(
      sanitizePersistedState(JSON.parse(serializedState)),
      initialState
    )
  } catch (e) {
    return undefined
  }
}

const defaultLanguage = {
  intl: {
    defaultLocale: 'es',
    locale: 'es',
    messages: msEs,
  },
}

const persistedState = loadFromLocalStorage()

const store = createStore(
  rootReducer,
  persistedState
    ? persistedState
    : defaultLanguage,
  process.env.NODE_ENV === 'development'
    ? compose(applyMiddleware(thunk), window.__REDUX_DEVTOOLS_EXTENSION__ ? window.__REDUX_DEVTOOLS_EXTENSION__() : f => f)
    : applyMiddleware(thunk)
)

store.subscribe(() => saveToLocalStorage(store.getState()))

export default store
